import { rem } from 'polished'
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import zIndex from 'styles/tools/z-index'
import Heading from 'LuxKit/Typography/Heading'
import LayoutContainer from 'components/Common/LayoutContainer'
import cx from 'clsx'
import YouMayAlsoLikeOffersCarousel from 'components/Recommendations/YouMayAlsoLike/Shared/YouMayAlsoLikeOffersCarousel'
import TrackingContext from 'contexts/trackingContext'
import { calcDistanceBetweenOffers } from 'lib/offer/offerUtils'
import { DistanceUnit } from 'lib/geo/distanceUnits'
import { take } from 'lib/array/arrayUtils'
import useOffers from 'hooks/Offers/useOffers'
import TextButton from 'LuxKit/Button/TextButton'
import Group from 'components/utils/Group'
import * as Analytics from 'analytics/analytics'

export const YMAL_FOOTER_HEIGHT_VAR = '--ymal-footer-height'

const Root = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: ${props => props.theme.palette.neutral.default.eight};
  padding-bottom: ${rem(24)};
  box-shadow: ${props => props.theme.shadow.top.large};
  border-radius: ${props => props.theme.borderRadius.M} ${props => props.theme.borderRadius.M} 0 0;
  z-index: ${zIndex.stickyFooter};
  transition-property: transform;
  transition-duration: .3s;
  transition-timing-function: ease-in;
  transition-delay: 0s;

  &.animation-immediate {
    transition-duration: 0s;
  }

  &.hidden {
    transform: translateY(100%);
  }
`

const HeaderGroup = styled(Group)`
  padding-top: ${rem(16)};
  padding-bottom: ${rem(16)};
`

type AnimationSpeed = 'gradual' | 'immediate'
export const LERE_YMAL_STICKY_FOOTER_PAGE_SIZE = 3

export interface Props {
  animationSpeed: AnimationSpeed
  distanceUnit?: DistanceUnit
  filters?: App.OfferListFilters
  hasEnoughRecommendationsToDisplay: boolean
  hidden: boolean
  loadingError?: any
  loadingState: App.ApiResponseState
  seedOffer?: App.Offer | App.BedbankOffer
  offerIds: Array<string>
}

function YmalStickyFooter({
  animationSpeed,
  distanceUnit,
  filters,
  hasEnoughRecommendationsToDisplay,
  hidden,
  loadingState,
  seedOffer,
  offerIds,
}: Props) {
  const [minimised, setMinimised] = useState(false)
  const [mounted, setMounted] = useState(false)
  const [offset, setOffset] = useState(0)
  const rootRef = useRef<HTMLDivElement>(null)
  const headerRef = useRef<HTMLDivElement>(null)
  const trackingContextExternal = useContext(TrackingContext)

  const handleOfferClick = useCallback(() => {
    Analytics.trackClientEvent({
      subject: 'sticky-footer-product-clicked',
      action: 'click',
      category: 'logging',
      type: 'interaction',
      optimizelyEventId: '5641781992226816',
      optimizelyEventKey: 'sticky_footer_product_click',
    })
  }, [])

  const [offers, offerDetailsLoading] = useOffers<App.HotelOffer | App.TourOffer | App.BedbankOffer>(take(offerIds, 3))

  useEffect(() => {
    if (!mounted && hasEnoughRecommendationsToDisplay) {
      setMounted(true)
    }
  }, [mounted, hasEnoughRecommendationsToDisplay])

  useEffect(() => {
    if (typeof document === 'undefined') {
      return
    }

    const isVisible = mounted && !hidden

    if (!isVisible) {
      document.documentElement.style.setProperty(YMAL_FOOTER_HEIGHT_VAR, '0px')
      return
    }

    const height = minimised
      ? headerRef.current?.getBoundingClientRect().height ?? 0
      : rootRef.current?.getBoundingClientRect().height ?? 0

    document.documentElement.style.setProperty(YMAL_FOOTER_HEIGHT_VAR, `${height}px`)

    return () => {
      document.documentElement.style.setProperty(YMAL_FOOTER_HEIGHT_VAR, '0px')
    }
  }, [mounted, hidden, minimised])

  const onToggleHide = useCallback(() => {
    setMinimised(!minimised)
  }, [minimised])

  const onRootClick: React.MouseEventHandler<HTMLDivElement> = useCallback(() => {
    if (minimised) {
      setMinimised(false)
    }
  }, [minimised])

  const style = useMemo<React.CSSProperties>(() => {
    if (!minimised || hidden) {
      return {}
    }

    try {
      // saved the offset already when customers view the page firstly, dont need to compute again
      if (offset === 0 && rootRef.current && headerRef.current) {
        // when minimizing, we want to slide down by (total height - header height), so that only header is visible
        setOffset(rootRef.current.getBoundingClientRect().height - headerRef.current.getBoundingClientRect().height)
      }
    } catch {
    }

    return {
      transform: `translateY(${offset}px)`,
    }
  }, [hidden, minimised, offset])

  const offerDistanceFrom = seedOffer?.property?.name

  const offerDistances = useMemo(() => {
    const offerIdDistances: Record<string, number | null> = {}
    if (!seedOffer) {
      return offerIdDistances
    }
    offers.forEach((offer) => {
      offerIdDistances[offer.id] = calcDistanceBetweenOffers(offer, seedOffer)
    })
    return offerIdDistances
  }, [offers, seedOffer])

  if (!hasEnoughRecommendationsToDisplay || !['done', 'error'].includes(loadingState) || offerDetailsLoading) {
    return null
  }

  return (
    <Root
      className={cx({
        hidden: hidden || !mounted,
        'animation-immediate': animationSpeed === 'immediate',
      })}
      style={style}
      ref={rootRef}
      onClick={onRootClick}
      role="button"
      data-testid="lere-sticky-footer"
    >
      <LayoutContainer>
        <HeaderGroup ref={headerRef} direction="horizontal" verticalAlign="center" horizontalAlign="space-between">
          <Heading variant="heading5">You may also like</Heading>
          <TextButton data-testid="ymal-hide-toggle" onClick={onToggleHide} kind="tertiary" size="small" horizontalOutdent="end">
            {minimised ? 'Show' : 'Hide'}
          </TextButton>
        </HeaderGroup>
        <YouMayAlsoLikeOffersCarousel
          offers={offers}
          offerDistances={offerDistances}
          offerDistanceFrom={offerDistanceFrom}
          offerDistanceUnit={distanceUnit}
          tracking={trackingContextExternal}
          filters={filters}
          pageSize={LERE_YMAL_STICKY_FOOTER_PAGE_SIZE}
          onTileClick={handleOfferClick}
        />
      </LayoutContainer>
    </Root>
  )
}

export default YmalStickyFooter
